<template>
  <InputWrapper
    v-slot="{ inputId }"
    :input-id="id"
    :label="
      t('common.assignedParam', {
        param: t('users.users', 2),
      })
    "
    class="assigned-users-filter"
    optional
  >
    <UserMultiSelect
      :id="inputId"
      v-model="internalValue.assignedUsers"
      :options="options"
      :placeholder="
        t('common.assignedParam', {
          param: t('users.users', 2),
        })
      "
      :virtual-scroller-options="{ itemSize: 48 }"
    />
  </InputWrapper>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { computed, onMounted, PropType, ref, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable, UserRef } from '@rygen/rygen-ui/model';

import api from '@/api';
import { OrderSearchCriteria, ShipmentSearchCriteria } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser.ts';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import UserMultiSelect from '@rygen/rygen-ui/components/form/UserMultiSelect.vue';

const internalValue = defineModel({
  type: Object as PropType<OrderSearchCriteria | ShipmentSearchCriteria>,
  required: true,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  entityType: {
    type: String as PropType<'shipment' | 'order'>,
    required: true,
  },
});
const { id, entityType } = toRefs(props);

const { selectedDomainId } = useCurrentUserStore();
const { t } = useI18n();
const toast = useToast();

const assignableUsers: Ref<Loadable<UserRef[]>> = ref({ loading: false });
const noUsersUser = new UserRef({
  username: 'no-users-filter',
  displayName: t('common.noParam', {
    param: t('common.assignedParam', {
      param: t('users.users', 2),
    }),
  }),
});

const options = computed(() => {
  const existingUsers = assignableUsers.value.data ?? [];
  return {
    loading: assignableUsers.value.loading,
    data: [noUsersUser, ...existingUsers],
  };
});

const loadAssignableUsers = async () => {
  if (!selectedDomainId.value) {
    console.error('Cannot determine current domain', selectedDomainId.value);
    return;
  }

  // Not the shared assignable-user store: the filter also lists internal assignees, which the
  // assign pickers must not offer.
  assignableUsers.value = { loading: true };
  try {
    assignableUsers.value = {
      loading: false,
      data: await api.user.findAllAssignableUsersForDomain(
        selectedDomainId.value,
        entityType.value,
        true,
      ),
    };
  } catch (e) {
    assignableUsers.value = { loading: false, error: e };
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading assignable users', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('users.users', 2),
      }),
      error: e,
    });
  }
};

onMounted(() => {
  loadAssignableUsers();
});
</script>
